import { Phone, CheckCircle, Briefcase, XCircle, ShieldCheck, DollarSign, CreditCard, Clock, TrendingUp, Wallet } from 'lucide-react'
import type { SummaryData } from '../types'
import { cn } from '@/lib/utils'

interface SummaryTablesProps {
  data: SummaryData
}

function fmt(val: number): string {
  return `$${val.toLocaleString('en-US')}`
}

interface CardProps {
  title: string
  icon: React.ElementType
  value: string | number
  subtitle: string
  iconColor?: string
  valueColor?: string
}

function Card({ title, icon: Icon, value, subtitle, iconColor = 'text-gray-400', valueColor = 'text-gray-900' }: CardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
      <div className="flex items-start justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <Icon className={cn('w-5 h-5', iconColor)} />
      </div>
      <p className={cn('text-3xl font-bold', valueColor)}>{value}</p>
      <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
    </div>
  )
}

export function SummaryTables({ data }: SummaryTablesProps) {
  return (
    <div className="space-y-4">
      {/* Row 1: Lead Funnel */}
      <div className="grid grid-cols-5 gap-4">
        <Card
          title="Calls"
          icon={Phone}
          value={data.calls}
          subtitle="Total eLocal leads"
          iconColor="text-blue-500"
        />
        <Card
          title="Billable"
          icon={ShieldCheck}
          value={data.billable}
          subtitle={`${fmt(data.cost_per_lead)} per lead`}
          iconColor="text-indigo-500"
          valueColor="text-indigo-600"
        />
        <Card
          title="Booked"
          icon={CheckCircle}
          value={data.booked}
          subtitle={`${fmt(data.cost_per_booked)} per booked`}
          iconColor="text-purple-500"
          valueColor="text-purple-600"
        />
        <Card
          title="Cancelled"
          icon={XCircle}
          value={data.cancelled}
          subtitle={`${data.booked > 0 ? ((data.cancelled / data.booked) * 100).toFixed(0) : 0}% cancel rate`}
          iconColor="text-red-500"
          valueColor="text-red-500"
        />
        <Card
          title="Jobs"
          icon={Briefcase}
          value={data.jobs}
          subtitle={`${fmt(data.cost_per_job)} per job`}
          iconColor="text-green-500"
          valueColor="text-green-600"
        />
      </div>

      {/* Row 2: Financial */}
      <div className="grid grid-cols-5 gap-4">
        <Card
          title="Spent"
          icon={DollarSign}
          value={fmt(data.spent)}
          subtitle="Billable leads cost"
          iconColor="text-red-500"
          valueColor="text-red-600"
        />
        <Card
          title="Collected"
          icon={CreditCard}
          value={fmt(data.collected)}
          subtitle="Paid payments"
          iconColor="text-green-500"
          valueColor="text-green-600"
        />
        <Card
          title="Outstanding"
          icon={Clock}
          value={fmt(data.outstanding)}
          subtitle="Revenue - Collected"
          iconColor="text-amber-500"
          valueColor="text-amber-600"
        />
        <Card
          title="Revenue"
          icon={TrendingUp}
          value={fmt(data.revenue)}
          subtitle="Job totals (non-cancelled)"
          iconColor="text-blue-500"
        />
        <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-500">Company Net</h3>
            <Wallet className="w-5 h-5 text-emerald-500" />
          </div>
          <p className="text-3xl font-bold text-emerald-600">{fmt(data.company_net)}</p>
          <div className="text-xs text-gray-400 mt-1 space-y-0.5">
            <p>{fmt(data.revenue)} - 10% parts = {fmt(data.after_parts)}</p>
            <p>{fmt(data.after_parts)} / 2 = <span className="font-semibold text-emerald-600">{fmt(data.company_net)}</span></p>
          </div>
        </div>
      </div>
    </div>
  )
}
